/**
 * Item Sources Settings Page
 *
 * Allows workspace admins to customize the list of sources shown on the
 * idea submission form. Builtin sources can be activated/deactivated;
 * custom sources can be added, renamed, or deleted.
 */

import { useEffect, useState } from 'react';
import { Tag, Plus, Trash2, GripVertical, Check, X, Edit2 } from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import { useItemSourcesStore } from '../../../stores/prioritization/itemSourcesStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import type { WorkspaceItemSource } from '../../../lib/itemSourcesService';

// ============================================================================
// Source Row
// ============================================================================

interface SourceRowProps {
  source: WorkspaceItemSource;
  onToggle: (id: string, active: boolean) => void;
  onRename: (id: string, label: string) => void;
  onDelete: (id: string) => void;
}

function SourceRow({ source, onToggle, onRename, onDelete }: SourceRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(source.label);

  const commitRename = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== source.label) {
      onRename(source.id, trimmed);
    } else {
      setDraft(source.label);
    }
    setEditing(false);
  };

  return (
    <div
      className={`flex items-center gap-3 p-3  border transition-colors ${
        source.isActive
          ? 'bg-[var(--bg-raised)] border-[var(--rule)]'
          : 'bg-[var(--surface)] border-[var(--rule)] opacity-60'
      }`}
    >
      <GripVertical className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />

      {/* Active toggle */}
      <button
        onClick={() => onToggle(source.id, !source.isActive)}
        className={`flex-shrink-0 w-9 h-5 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 ${
          source.isActive ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'
        }`}
        title={source.isActive ? 'Deactivate source' : 'Activate source'}
        aria-checked={source.isActive}
        role="switch"
      >
        <span
          className={`block w-4 h-4 mx-0.5 my-0.5 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${
            source.isActive ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>

      {/* Label */}
      <div className="flex-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') { setDraft(source.label); setEditing(false); }
            }}
            className="w-full px-2 py-1 text-sm border border-primary-400 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        ) : (
          <span className="text-sm font-medium text-[var(--text-pri)]">{source.label}</span>
        )}
        <span className="block text-xs text-[var(--text-ter)] font-mono">{source.value}</span>
      </div>

      {/* Builtin badge */}
      {source.isBuiltin && (
        <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-[var(--hover-tint)] text-[var(--text-ter)] rounded-full">
          builtin
        </span>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1 flex-shrink-0">
        {!editing && (
          <button
            onClick={() => { setDraft(source.label); setEditing(true); }}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded hover:bg-[var(--hover-tint)]"
            title="Rename"
          >
            <Edit2 className="h-3.5 w-3.5" />
          </button>
        )}
        {!source.isBuiltin && (
          <button
            onClick={() => onDelete(source.id)}
            className="p-1.5 text-[var(--text-ter)] hover:text-[var(--coral)] rounded hover:bg-[var(--coral-dim)]"
            title="Delete custom source"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Add Source Form
// ============================================================================

interface AddSourceFormProps {
  workspaceId: string;
  onAdd: (workspaceId: string, label: string) => Promise<void>;
}

function AddSourceForm({ workspaceId, onAdd }: AddSourceFormProps) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    const trimmed = label.trim();
    if (!trimmed) return;
    setSaving(true);
    await onAdd(workspaceId, trimmed);
    setSaving(false);
    setLabel('');
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-600 border border-primary-300 hover:bg-primary-50 transition-colors"
      >
        <Plus className="h-4 w-4" />
        Add custom source
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 p-3 border border-primary-300 bg-primary-50">
      <input
        autoFocus
        type="text"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleAdd();
          if (e.key === 'Escape') { setLabel(''); setOpen(false); }
        }}
        placeholder="e.g. Customer Interview"
        className="flex-1 px-3 py-1.5 text-sm border border-[var(--rule)] focus:ring-2 focus:ring-primary-500 focus:border-transparent"
      />
      <button
        onClick={handleAdd}
        disabled={!label.trim() || saving}
        className="p-1.5 text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
        title="Add source"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        onClick={() => { setLabel(''); setOpen(false); }}
        className="p-1.5 text-[var(--text-ter)] hover:bg-[var(--hover-tint)]"
        title="Cancel"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// ============================================================================
// Main Page
// ============================================================================

export default function ItemSources() {
  const workspaceId = getCurrentWorkspaceId();
  const addNotification = useUIStore((s) => s.addNotification);
  const { sources, loading, error, fetchSources, toggleActive, addSource, renameSource, removeSource } =
    useItemSourcesStore();

  useEffect(() => {
    if (workspaceId) fetchSources(workspaceId);
  }, [workspaceId, fetchSources]);

  const handleToggle = async (id: string, isActive: boolean) => {
    await toggleActive(id, isActive);
    addNotification({
      type: 'success',
      title: isActive ? 'Source activated' : 'Source deactivated',
      message: `The source is now ${isActive ? 'offered' : 'no longer offered'} when people pick one.`,
      duration: 3000,
    });
  };

  const handleRename = async (id: string, label: string) => {
    await renameSource(id, label);
    addNotification({ type: 'success', title: 'Source renamed', message: '', duration: 2000 });
  };

  const handleDelete = async (id: string) => {
    const source = sources.find((s) => s.id === id);
    if (!source || source.isBuiltin) return;
    await removeSource(id);
    addNotification({
      type: 'success',
      title: 'Source deleted',
      message: `"${source.label}" has been removed.`,
      duration: 3000,
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<Tag className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Idea Sources"
        subtitle="Where an item's idea came from, and how it arrived. Each item carries one, shown and changed on the item page; arrivals set it by themselves."
      />

      {!workspaceId && (
        <div className="card bg-[var(--mustard-dim)] border-[var(--mustard)]">
          <p className="text-sm text-[var(--mustard)]">Select a workspace in the sidebar to manage its idea sources.</p>
        </div>
      )}

      {error && (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <p className="text-sm text-[var(--coral)]">{error}</p>
        </div>
      )}

      {workspaceId && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between mb-1">
            <div>
              <h3 className="text-sm font-semibold text-[var(--text-pri)]">Sources</h3>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                {sources.filter((s) => s.isActive).length} of {sources.length} active
              </p>
            </div>
          </div>

          {loading && sources.length === 0 ? (
            <div className="text-sm text-[var(--text-ter)] py-4 text-center">Loading sources…</div>
          ) : (
            <div className="space-y-5">
              {(
                [
                  ['idea', 'Where ideas come from', 'People pick one when they submit an idea or edit an item.'],
                  ['arrival', 'How items arrive', 'Set by itself when an item arrives this way — by import, a connector, a queue or a session. People can pick these too.'],
                ] as const
              ).map(([kind, title, hint]) => {
                const rows = sources.filter((s) => s.kind === kind);
                if (rows.length === 0) return null;
                return (
                  <div key={kind} data-testid={`item-sources-${kind}`}>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)]">{title}</p>
                    <p className="mb-2 text-xs text-[var(--text-ter)]">{hint}</p>
                    <div className="space-y-2">
                      {rows.map((source) => (
                        <SourceRow
                          key={source.id}
                          source={source}
                          onToggle={handleToggle}
                          onRename={handleRename}
                          onDelete={handleDelete}
                        />
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <div className="pt-2 border-t border-[var(--rule)]">
            <AddSourceForm workspaceId={workspaceId} onAdd={addSource} />
          </div>
        </div>
      )}

      <div className="card bg-[var(--slate-dim)] border-[var(--slate)]">
        <p className="text-sm text-[var(--text-sec)]">
          <strong>Tip:</strong> Built-in sources can be switched off but not deleted; your own can be deleted.
          The idea submission form offers only the sources under “Where ideas come from”.
        </p>
      </div>
    </div>
  );
}
